import { ApiClient, ApiSchema } from "./ApiClient";
import { AssetFormData } from "@/formTypes/AssetFormDataType";
import { nanoid } from 'nanoid/non-secure';
import { uploadData, remove as removeS3Asset } from 'aws-amplify/storage';
import { DynamicFormFile } from "@mavichpe/dynamic-forms";

export type AssetDataType = Partial<ApiSchema["Asset"]["type"] & AssetFormData>;
export type AssetRelatedModelType = 'suscription' | 'suscriptionP12' | 'suscriptionLogo2' | 'suscriptionLogo3' | 'suscriptionWatermark' | 'productImage';

export const fetchAsset = async (assetId: string): Promise<AssetDataType> => {
    try {
        const { data, errors } = await ApiClient.models.Asset.get({ id: assetId })

        if (errors) {
            console.error("Errores al obtener las suscripciones:", errors);
            return {};
        }

        return data || {};
    } catch (error) {
        console.error("Error en el fetch de suscripciones:", error);
        return {};
    }
};

export const saveAsset = async (data: AssetDataType, s3PathPrefix: string): Promise<ApiSchema["Asset"]["type"] | null> => {
    const isNewAsset = data && data?.id ? false : true;
    if (data._toDelete && data.path && data.id) {
        const deleteAssetResponse = await deleteAsset(data.id, data.path);
        return deleteAssetResponse;
    }

    if (isNewAsset) {
        if (data.fileData) {
            const s3FilePath = await uploadFileToS3(data.fileData, s3PathPrefix);
            data.path = s3FilePath;
            delete data.fileData
        }

        const response = await ApiClient.models.Asset.create(data);
        return response.data
    } else {
        const response = await ApiClient.models.Asset.update(
            {
                id: `${data.id}`,
                ...data
            }
        );
        return response.data
    }
};

export const saveRelateAsset = async <T, K extends keyof T>(data: T, property: K, files: Array<DynamicFormFile>, s3Folder: string, model: AssetRelatedModelType, relatedId: string): Promise<T> => {
    let newData = { ...data }
    const newFiles = files.filter(l => !l._toDelete)
    const toDeleteAsset = files.filter(l => l._toDelete)

    if (newFiles.length == 0) {
        newData = {
            ...newData,
            [property]: ""
        }
    } else {
        for (const a of newFiles) {
            const asset = {
                name: a.name,
                path: a.path,
                type: a.type,
                size: a.size,
                fileData: a,
                [`${model}Id`]: relatedId,

            }
            const filedata = await saveAsset(asset, s3Folder)
            newData = {
                ...newData,
                [property]: filedata?.path
            }
        }
    }

    for (const a of toDeleteAsset) {
        if (a.id && a.path) {
            await deleteAsset(a.id, a.path)
        }
    }

    return newData;
};


const uploadFileToS3 = async (file: DynamicFormFile, s3PathPrefix: string = ""): Promise<string> => {
    if (file) {
        const s3AssetPrefixId = nanoid(10);
        const fileName = s3PathPrefix + s3AssetPrefixId + "-" + file.name;
        const operation = uploadData({
            path: fileName,
            data: file.data,
            options: {
                contentType: file.type,
            }
        });
        await operation.result;
        return fileName;
    } else {
        return ""
    }
}

const deleteFileToS3 = async (s3Path: string) => {
    await removeS3Asset({
        path: s3Path,
    });
}

export const deleteAsset = async (assetId: string, filePath: string): Promise<ApiSchema["Asset"]["type"] | null> => {
    if (assetId) {
        const deletedAsset = await ApiClient.models.Asset.delete({
            id: assetId,
        });
        if (filePath) {
            await deleteFileToS3(filePath);
        }

        return deletedAsset.data;
    }
    return null
}